import {
    type ClientRect,
    type CollisionDetection,
    defaultDropAnimationSideEffects,
    DndContext,
    DragOverlay,
    type DropAnimation,
    getClientRect,
    getScrollableAncestors,
    PointerSensor,
    type UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { PlusIcon } from 'components/icons';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { ENDING_MS, GLIDE, GLIDE_SPRING } from 'layout/NewDesignLayout/Sidebar/usePinDrag';
import { m, type MotionProps } from 'motion/react';
import { type ComponentProps, memo, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { HomeEntry } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { ArrangeBar } from './ArrangeBar';
import {
    addsGap,
    entryKey,
    entryName,
    moveEntryBy,
    moveEntryTo,
    removeEntry,
    resizeEntry,
    SIZE_HEIGHT,
    SIZE_SPAN,
    sizesOf,
} from './layout';
import { arrive, atRest, hold, restOf, reveal, setOff, settle, slotInReach, turnUp } from './motion';

/** What arranging needs from the page: the edited list back, and the way to the Add a widget dialog. */
export type Arranging = {
    onChange: (entries: HomeEntry[]) => void;
    onAdd: () => void;
};

type Props = {
    /** The entries in the order the grid places them. */
    entries: HomeEntry[];
    /** What an entry shows. */
    renderEntry: (entry: HomeEntry) => ReactNode;
    /** Present while this grid is being arranged. */
    arranging?: Arranging;
};

/** The widget in hand: its key, the place in the list the pointer gives it, and the copy that follows the pointer. */
type Drag = { key: string; to: number; lifted: Node[] };

// The cells take no shift from the drag itself: the list is reordered, the grid flows it, and each cell glides to
// where that leaves it.
function noShift() {
    return null;
}

/**
 * How a widget let go lands: the pins' glide onto the slot, which is back where it was lifted from when the release
 * is out of every slot's reach. The tilt goes on the way, so it lands as the cell drawn under it.
 */
const DROP: DropAnimation = {
    ...GLIDE,
    sideEffects: (args) => {
        args.dragOverlay.node.firstElementChild?.animate({ rotate: '0deg' }, { ...GLIDE, fill: 'forwards' });
        // The slot may still be showing up where the last change of slot put it: the cell stays unseen under the
        // widget landing on it. By a class that outranks the opacity Motion writes back on the cell as it draws it.
        settle(args.active.node);
        const show = defaultDropAnimationSideEffects({ className: { active: 'opacity-0!' } })(args);
        // The cell shows as the copy goes, in one frame: React takes the copy away only a moment later.
        return () => {
            show?.();
            args.dragOverlay.node.style.visibility = 'hidden';
        };
    },
};

// The copy is measured by the overlay around it, which it fills untilted: the tilted copy's own box is larger, and the
// drop would land it that much off the cell.
const MEASURING = { dragOverlay: { measure: (node: HTMLElement) => getClientRect(node.parentElement ?? node) } };

/** What Motion makes of a cell: it glides to where a change leaves it if it `travels`, and else turns up there. */
type Moves = (cell: () => HTMLElement | null, travels: boolean) => MotionProps;

type CellProps = {
    entry: HomeEntry;
    children: ReactNode;
    moves: Moves;
    /** The cell's controls while arranging. */
    bar?: Pick<
        ComponentProps<typeof ArrangeBar>,
        'touch' | 'canMoveUp' | 'canMoveDown' | 'leavesGap' | 'onMove' | 'onResize' | 'onRemove'
    >;
    /**
     * Removed, and on its way out, in the box it had: out of reach and unnamed, it is no longer one of the grid's
     * entries, and out of the grid's flow, so the others can close up under it.
     */
    leaving?: ReturnType<typeof restOf>;
};

function Cell({ entry, children, moves, bar, leaving }: CellProps) {
    const { setNodeRef, setActivatorNodeRef, listeners, isDragging, node } = useSortable({
        id: entryKey(entry),
        disabled: !bar || !!leaving,
        animateLayoutChanges: () => false,
    });
    const name = entryName(entry);

    return (
        <m.div
            ref={setNodeRef}
            // The slot a held widget lands in shows up in each place the pointer gives it.
            {...moves(() => node.current, !isDragging)}
            data-entry={leaving ? undefined : entryKey(entry)}
            inert={!!leaving}
            aria-hidden={!!leaving}
            style={leaving}
            className={cn(
                'relative min-w-0 rounded-[10px]',
                leaving ? 'absolute' : SIZE_SPAN[entry.size],
                entry.kind === 'WIDGET' && SIZE_HEIGHT[entry.size],
                // Brought back into sight, its controls come with it: they stand over its top edge.
                bar && 'scroll-mt-6 scroll-mb-2 max-sm:scroll-mt-12',
                bar && !isDragging && 'outline-border-dashed outline-[1.5px] outline-offset-4 outline-dashed',
                isDragging && 'border-changed-border bg-changed border-[1.5px] border-dashed',
                // The pins' own ending, a quarter as wide on a box this size: a pin let go out of Pinned goes in
                // this puff.
                bar && '[--pin-bounce:0.25]',
                leaving && 'pin-puff'
            )}
        >
            {/*
                The same node whether arranging or not, so a section keeps its state across Customise and Done. It
                stacks on its own, under the controls that follow it.
            */}
            <div
                data-slot="widget"
                inert={!!bar}
                className={cn('isolate flex h-full min-w-0 flex-col gap-2', isDragging && 'invisible')}
            >
                {children}
            </div>
            {bar ? (
                <>
                    {isDragging ? (
                        <span
                            aria-hidden
                            className="text-changed-ink text-meta absolute inset-x-0 bottom-2.5 text-center font-medium"
                        >
                            {t('home.widgets.lands-here')}
                        </span>
                    ) : null}
                    {/* Out of sight in the slot, yet mounted: the drag runs from its handle. */}
                    <ArrangeBar
                        {...bar}
                        name={name}
                        size={entry.size}
                        sizes={sizesOf(entry)}
                        handleRef={setActivatorNodeRef}
                        handleListeners={listeners}
                        className={isDragging ? 'invisible' : undefined}
                    />
                </>
            ) : null}
        </m.div>
    );
}

/**
 * What an entry shows. Drawn again only when the entry or the page's way of drawing it changes: a drag draws the
 * grid again at every change of slot, which the widgets, their lists and their charts have no part in.
 */
const Content = memo(function Content({ entry, render }: { entry: HomeEntry; render: Props['renderEntry'] }) {
    return render(entry);
});

// The others set off as a removed widget's puff nears its end, so that no hole stands still between the two.
const CLOSE_UP_AFTER = ENDING_MS.puff * 0.75;

// A widget added while arranging comes in from a little smaller as it fades in.
const ARRIVING = { opacity: 0, scale: 0.9 };
const ARRIVED = { opacity: 1, scale: 1 };

/**
 * Home's grid: three columns from `lg`, two from `sm`, one below, in rows of 120px (112px while arranging). An entry
 * spans its size and the list's order is the only placement, so the same layout holds at every width. While
 * arranging, each cell carries its controls and a drag reorders the list; nothing is saved here.
 *
 * Arranging moves as the sidebar's pins do, and not at all for a person who asked for reduced motion: the cells
 * glide to wherever a change leaves them, a widget let go within reach of a slot is pulled into it with one
 * wiggle, and a removed one goes in a puff as the others close up. Only widgets travel: the slot and the
 * tile that adds a widget turn up where a change puts them.
 */
export function WidgetGrid({ entries, renderEntry, arranging }: Props) {
    const isMobile = useIsMobile();
    const still = useReducedMotion();
    const gridRef = useRef<HTMLDivElement>(null);
    const [drag, setDrag] = useState<Drag | null>(null);
    // The widget just removed, with the place it had in the list and on the grid, for as long as its puff plays.
    const [leaving, setLeaving] = useState<{ entry: HomeEntry; at: number; box: ReturnType<typeof restOf> } | null>(
        null
    );
    // The copy of the widget in hand, which lands where it is let go.
    const liftedRef = useRef<HTMLDivElement>(null);
    // Where each cell stood when the drag began. The cells reflow under the pointer as the slot moves, so reading
    // the target from where they stand now would send the slot back and forth.
    const startRects = useRef<Map<UniqueIdentifier, ClientRect> | null>(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const keys = entries.map(entryKey);
    const placed = drag ? moveEntryTo(entries, drag.key, drag.to) : entries;
    const going = arranging && leaving && !keys.includes(entryKey(leaving.entry)) ? leaving : null;
    const shown = going ? [...placed.slice(0, going.at), going.entry, ...placed.slice(going.at)] : placed;

    useEffect(() => {
        if (!leaving) return undefined;
        const timer = setTimeout(() => setLeaving(null), ENDING_MS.puff);
        return () => clearTimeout(timer);
    }, [leaving]);

    const on = !!arranging && !still;
    const wait = going ? CLOSE_UP_AFTER : 0;
    // The fades and cuts of a cell that turns up or changes size, which no hand moves: a tween.
    const timing = { duration: 0.18, ease: 'easeOut', delay: wait / 1000 } as const;
    // The grid as last drawn. Its `shape` is what Motion follows: it measures the cells, and moves them, only as that
    // changes, which is only for a change made while the grid is on and already was. Neither arranging beginning or
    // ending nor a change made with the grid off, as Cancel's, is measured: the cells are drawn at once where it leaves
    // them. A cell first drawn while the grid is on and already was has just been added, and arrives. A count of those
    // changes, not the list: a move made again after Cancel would leave the list as Motion last saw it.
    const now = JSON.stringify([shown.map((entry) => [entryKey(entry), entry.size]), going?.at]);
    const [drawn, setDrawn] = useState({ on, now, shape: 0, kept: false });
    if (drawn.on !== on || drawn.now !== now) {
        const kept = on && drawn.on;
        setDrawn({ on, now, shape: drawn.shape + Number(kept), kept });
    }
    const moves: Moves = (cell, travels) => ({
        layout: 'position',
        layoutDependency: drawn.shape,
        initial: drawn.kept ? ARRIVING : false,
        animate: ARRIVED,
        transition: {
            ...timing,
            // A cell that does not travel is there at once, though not in no time at all: Motion skips a move of no
            // length, and its onLayoutAnimationStart with it.
            layout: travels ? { ...GLIDE_SPRING, delay: timing.delay } : { ...timing, duration: 0.001 },
        },
        onLayoutAnimationStart: () => {
            const node = cell();
            if (!node) return;
            if (travels) setOff(node, GLIDE.duration + wait);
            else turnUp(node, timing);
        },
        onLayoutAnimationComplete: () => {
            const node = cell();
            if (node) arrive(node);
        },
    });
    // The Add tile, which turns up wherever the last widget leaves it.
    const [addTile, setAddTile] = useState<HTMLElement | null>(null);

    // After a render that resized a cell.
    useLayoutEffect(() => {
        if (gridRef.current) reveal(gridRef.current, timing);
    });

    // What a pressed control must leave in sight: a cell, or the controls that took the focus of a removed one.
    const moved = useRef<Element | null>(null);
    const asks = useRef(0);
    /**
     * Brings what a pressed control sent out of sight back into it, by the least scroll that shows it whole: none
     * when it already is. It waits for the cells to be at rest, since one on its way still shows where it was, and
     * only the last press counts. A widget taller than the window cannot show whole: its controls, which hold the
     * focus, are then what is shown. Smoothly, or at once for a person who asked for reduced motion.
     */
    const follow = () => {
        const grid = gridRef.current;
        const target = moved.current;
        if (!grid || !target) return;
        moved.current = null;
        const ask = ++asks.current;
        const show = () => {
            if (ask !== asks.current) return;
            const port = getScrollableAncestors(target, 1)[0] ?? document.documentElement;
            const controls = target.querySelector(':scope > [data-arrange-bar]');
            // With the room the cell keeps above itself for its controls, and under itself for its outline.
            const { scrollMarginTop, scrollMarginBottom } = getComputedStyle(target);
            const room = (parseFloat(scrollMarginTop) || 0) + (parseFloat(scrollMarginBottom) || 0);
            const fits = target.getBoundingClientRect().height + room <= port.clientHeight;
            (controls && !fits ? controls : target).scrollIntoView({
                block: 'nearest',
                behavior: still ? 'instant' : 'smooth',
            });
        };
        void atRest(grid).then(() => {
            // A focus still on its way back, as from a closing Size menu, would cut a scroll under way short: the
            // scroll is asked once that focus has landed and done its own.
            if (target.contains(document.activeElement)) show();
            else target.addEventListener('focusin', () => requestAnimationFrame(show), { once: true });
        });
    };
    // After the render a press caused; and not while a removed widget's puff plays, the cells having yet to close up.
    useEffect(() => {
        if (!going) follow();
    });
    const cellOf = (key: string) => gridRef.current?.querySelector<HTMLElement>(`[data-entry="${key}"]`);

    // The magnet: the slot is the cell, where it stood when the drag began, that the pointer is in reach of.
    const collisionDetection: CollisionDetection = ({ active, droppableRects, pointerCoordinates }) => {
        if (!startRects.current && droppableRects.size > 0) {
            startRects.current = new Map(droppableRects);
        }
        const held = drag ? keys[drag.to] : active.id;
        const slots = startRects.current ?? droppableRects;
        const slot = pointerCoordinates
            ? slotInReach(pointerCoordinates.x, pointerCoordinates.y, slots, held)
            : undefined;
        return slot === undefined ? [] : [{ id: slot }];
    };

    const endDrag = () => {
        setDrag(null);
        startRects.current = null;
    };

    const move = (key: string, by: -1 | 1) => {
        // Rendered at once so the focus can follow the widget: the browser drops it from a cell that moves.
        flushSync(() => arranging?.onChange(moveEntryBy(entries, key, by)));
        const cell = cellOf(key);
        const pressed = cell?.querySelector<HTMLElement>(`[data-move="${by}"]:not(:disabled)`);
        (pressed ?? cell?.querySelector<HTMLElement>('[data-move]:not(:disabled)'))?.focus();
        moved.current = cell ?? null;
        follow();
    };

    const resize = (key: string, size: HomeEntry['size']) => {
        const cell = cellOf(key);
        moved.current = cell ?? null;
        if (cell && !still) hold(cell);
        arranging?.onChange(resizeEntry(entries, key, size));
    };

    const remove = (key: string) => {
        const at = keys.indexOf(key);
        const cell = cellOf(key);
        flushSync(() => {
            if (cell && !still) setLeaving({ entry: entries[at], at, box: restOf(cell) });
            arranging?.onChange(removeEntry(entries, key));
        });
        // The focus goes to the controls of the widget that took the place, or of the one before it at the end of
        // the list; with no widget left, to the tile that adds one.
        const cells = gridRef.current?.querySelectorAll('[data-entry]') ?? [];
        const next = cells[Math.min(at, cells.length - 1)]?.querySelector<HTMLElement>(
            '[data-arrange-bar] button:not(:disabled):not([tabindex="-1"])'
        );
        const focused = next ?? gridRef.current?.querySelector<HTMLElement>(':scope > button');
        // Without the browser's own scroll to them: they are about to move up into the place that was freed, and
        // are brought into sight there, once the puff is over and their widget has closed up.
        focused?.focus({ preventScroll: true });
        moved.current = focused?.closest('[data-arrange-bar]') ?? focused ?? null;
        if (still) follow();
    };

    return (
        <DndContext
            sensors={sensors}
            measuring={MEASURING}
            collisionDetection={collisionDetection}
            onDragStart={({ active }) => {
                const key = String(active.id);
                const cell = gridRef.current?.querySelector(`[data-entry="${key}"]`);
                // ponytail: the lifted widget is a still copy of the cell, not a second live one. Its lists start
                // from the top and it does not update while held; mount the entry in the overlay if that shows.
                const lifted = [...(cell?.children ?? [])]
                    .filter((part) => !part.hasAttribute('data-arrange-bar'))
                    .map((part) => part.cloneNode(true));
                setDrag({ key, to: keys.indexOf(key), lifted });
                // A drag scrolls the page itself, at the edges.
                moved.current = null;
                asks.current += 1;
            }}
            // Out of every slot's reach, or over one that would leave a gap, the widget goes back to where it was
            // lifted from.
            onDragOver={({ over }) =>
                setDrag((current) => {
                    if (!current) return current;
                    const to = keys.indexOf(String(over?.id ?? current.key));
                    const gaps = addsGap(entries, moveEntryTo(entries, current.key, to));
                    return { ...current, to: gaps ? keys.indexOf(current.key) : to };
                })
            }
            onDragEnd={({ over }) => {
                arranging?.onChange(placed);
                // Let go into a slot, it wiggles at once, as a chip docks, while it lands: the cell under it stays
                // unseen until then. Sent back from a slot that would leave a gap, it lands as one let go out of
                // reach: no wiggle.
                if (over && keys.indexOf(String(over.id)) === drag?.to && !still) {
                    liftedRef.current?.classList.add('pin-wiggle');
                }
                endDrag();
            }}
            onDragCancel={endDrag}
        >
            <SortableContext items={keys} strategy={noShift}>
                <div
                    ref={gridRef}
                    className={cn(
                        // Positioned: a removed widget keeps the box it had in the grid on its way out.
                        'relative grid grid-cols-1 gap-(--card-gap) sm:grid-flow-dense sm:auto-rows-[7.5rem] sm:grid-cols-2 lg:grid-cols-3',
                        // On a phone the controls sit above each cell, the first cell's included. From `sm` they
                        // stand over the cell's top edge, drawn for the 24px gap on 7rem rows.
                        arranging && 'max-sm:gap-13 max-sm:pt-7 sm:auto-rows-[7rem] sm:gap-6',
                        // While the cells glide the page scrolls only when asked to. Left to anchor itself on a
                        // cell that a change moves, the browser shifts the whole page by that move in one frame,
                        // and the cell then glides back to where it was.
                        on && '[overflow-anchor:none]'
                    )}
                >
                    {shown.map((entry) => {
                        const key = entryKey(entry);
                        // Its place among the entries: none for the one that is leaving.
                        const index = placed.indexOf(entry);
                        return (
                            <Cell
                                key={key}
                                entry={entry}
                                moves={moves}
                                leaving={index === -1 ? going?.box : undefined}
                                bar={
                                    arranging && {
                                        touch: isMobile,
                                        canMoveUp: index > 0,
                                        canMoveDown: index !== -1 && index < placed.length - 1,
                                        leavesGap: (change) =>
                                            addsGap(
                                                entries,
                                                typeof change === 'number'
                                                    ? moveEntryBy(entries, key, change)
                                                    : resizeEntry(entries, key, change)
                                            ),
                                        onMove: (by) => move(key, by),
                                        onResize: (size) => resize(key, size),
                                        onRemove: () => remove(key),
                                    }
                                }
                            >
                                <Content entry={entry} render={renderEntry} />
                            </Cell>
                        );
                    })}
                    {arranging ? (
                        <m.button
                            ref={setAddTile}
                            {...moves(() => addTile, false)}
                            type="button"
                            onClick={arranging.onAdd}
                            className="border-border-dashed text-primary flex min-h-28 cursor-pointer items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-dashed font-medium max-sm:hidden"
                        >
                            <PlusIcon className="size-4" />
                            {t('home.widgets.add')}
                        </m.button>
                    ) : null}
                </div>
            </SortableContext>
            <DragOverlay dropAnimation={still ? null : DROP}>
                {drag ? (
                    <div
                        aria-hidden
                        className="shadow-drag relative h-full cursor-grabbing rounded-[10px] [--pin-bounce:0.25] motion-safe:-rotate-2"
                        ref={(node) => {
                            liftedRef.current = node;
                            node?.replaceChildren(...drag.lifted);
                        }}
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
